/**
 * SPO流程主页面
 *
 * 功能：
 * - 展示SPO整体进度和状态
 * - 三阶段流程可视化（EL、Evaluation、Result）
 * - 时间线展示关键节点
 * - 快速操作入口
 * - 邮件通信记录
 */

import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Steps,
  Descriptions,
  Timeline,
  Button,
  Space,
  Tag,
  Alert,
  Empty,
  Spin,
  Progress,
  Row,
  Col,
  Statistic,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  MailOutlined,
  DollarOutlined,
  FormOutlined,
  FileDoneOutlined,
  RightOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useGetSPOByProjectQuery } from '../../store/api/spoApi';
import { UnifiedStatusTag } from '../../components/unified/UnifiedStatusTag';

const { Step } = Steps;

interface SPOProcessPageProps {
  projectId?: string; // 可选，如果从路由参数获取
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as {
    status?: unknown;
    originalStatus?: unknown;
    data?: { status?: unknown };
  };
  if (typeof candidate.status === 'number') return candidate.status;
  if (typeof candidate.originalStatus === 'number')
    return candidate.originalStatus;
  if (typeof candidate.data?.status === 'number') return candidate.data.status;
  return undefined;
};

const pickMessageFromUnknown = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value instanceof Error && value.message) return value.message;
  if (typeof value === 'object') {
    const candidate = value as {
      message?: unknown;
      error?: unknown;
      details?: unknown;
    };
    return (
      pickMessageFromUnknown(candidate.message) ||
      pickMessageFromUnknown(candidate.error) ||
      pickMessageFromUnknown(candidate.details)
    );
  }
  return undefined;
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;

  const directMessage = pickMessageFromUnknown(error);
  if (directMessage) return directMessage;

  if (typeof error === 'object') {
    const candidate = error as {
      data?: unknown;
      error?: unknown;
      message?: unknown;
    };
    return (
      pickMessageFromUnknown(candidate.data) ||
      pickMessageFromUnknown(candidate.error) ||
      pickMessageFromUnknown(candidate.message) ||
      fallback
    );
  }

  return String(error) || fallback;
};

export const SPOProcessPage: React.FC<SPOProcessPageProps> = ({
  projectId: propProjectId,
}) => {
  const { projectId: paramProjectId } = useParams<{ projectId: string }>();
  const projectId = propProjectId || paramProjectId;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // 获取SPO数据
  const {
    data: spoResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetSPOByProjectQuery(projectId!, {
    skip: !projectId,
  });

  const spoData = spoResponse?.data;

  // 计算整体进度
  const overallProgress = useMemo(() => {
    if (!spoData) return 0;

    let totalProgress = 0;
    let phaseCount = 0;

    // Phase 1: EL
    if (spoData.engagementLetter) {
      totalProgress += spoData.engagementLetter.progress || 0;
      phaseCount++;
    }

    // Phase 2: Evaluation
    if (spoData.evaluation) {
      totalProgress += spoData.evaluation.progress || 0;
      phaseCount++;
    }

    // Phase 3: Result
    if (spoData.result) {
      totalProgress += spoData.result.progress || 0;
      phaseCount++;
    }

    return phaseCount > 0 ? Math.round(totalProgress / phaseCount) : 0;
  }, [spoData]);

  // 获取当前阶段
  const currentPhase = spoData?.currentPhase || 1;

  // 获取最近的时间线事件
  const recentEvents = useMemo(() => {
    if (!spoData?.globalTimeline) return [];
    return [...spoData.globalTimeline]
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      .slice(0, 5);
  }, [spoData]);

  // 获取快速操作
  const getQuickActions = () => {
    if (!spoData) return [];

    const actions: Array<{
      key: string;
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
      disabled?: boolean;
      type?: 'primary' | 'default';
    }> = [];

    // 根据当前状态和阶段提供快速操作
    if (spoData.currentPhase === 1) {
      if (spoData.engagementLetter.status === 'PENDING_GENERATION') {
        actions.push({
          key: 'generate-el',
          label: t(
            'project:taskContent.spoProcess.overview.quickActions.generateEngagementLetter'
          ),
          icon: <FileTextOutlined />,
          onClick: () => navigate(`/spo/${spoData._id}/engagement-letter`),
          type: 'primary',
        });
      } else if (spoData.engagementLetter.status === 'BRAVOWHALE_SIGNED') {
        actions.push({
          key: 'issuer-sign',
          label: t(
            'project:taskContent.spoProcess.overview.quickActions.issuerSign'
          ),
          icon: <FormOutlined />,
          onClick: () => navigate(`/spo/${spoData._id}/engagement-letter`),
          type: 'primary',
        });
      } else if (spoData.engagementLetter.status === 'ISSUER_SIGNED') {
        actions.push({
          key: 'send-to-sp',
          label: t(
            'project:taskContent.spoProcess.overview.quickActions.sendToSP'
          ),
          icon: <MailOutlined />,
          onClick: () => navigate(`/spo/${spoData._id}/engagement-letter`),
          type: 'primary',
        });
      }

      if (spoData.paymentProof?.status === 'PENDING') {
        actions.push({
          key: 'upload-payment',
          label: t(
            'project:taskContent.spoProcess.overview.quickActions.uploadPaymentProof'
          ),
          icon: <DollarOutlined />,
          onClick: () => navigate(`/spo/${spoData._id}/payment`),
        });
      }
    }

    if (spoData.currentPhase === 2) {
      if (spoData.evaluation.status === 'NOT_STARTED') {
        actions.push({
          key: 'submit-info',
          label: t(
            'project:taskContent.spoProcess.overview.quickActions.submitProjectInfo'
          ),
          icon: <FormOutlined />,
          onClick: () => navigate(`/spo/${spoData._id}/issuer-submission`),
          type: 'primary',
        });
      }
    }

    if (spoData.currentPhase === 3) {
      if (spoData.result.drafts && spoData.result.drafts.length > 0) {
        const latestDraft =
          spoData.result.drafts[spoData.result.drafts.length - 1];
        if (latestDraft.status === 'PENDING_REVIEW') {
          actions.push({
            key: 'submit-feedback',
            label: t(
              'project:taskContent.spoProcess.overview.quickActions.submitFeedback'
            ),
            icon: <FormOutlined />,
            onClick: () => navigate(`/spo/${spoData._id}/feedback`),
            type: 'primary',
          });
        }
      }
    }

    // 通用操作
    actions.push({
      key: 'view-emails',
      label: t(
        'project:taskContent.spoProcess.overview.quickActions.viewEmails'
      ),
      icon: <MailOutlined />,
      onClick: () => navigate(`/spo/${spoData._id}/emails`),
    });

    return actions;
  };

  // 渲染加载状态
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>
          {t('project:taskContent.spoProcess.overview.loading')}
        </div>
      </div>
    );
  }

  // 渲染错误状态
  if (isError) {
    const status = getErrorStatus(error);
    const fallbackMessage = t(
      'project:taskContent.spoProcess.overview.loadFailed'
    );
    const errorMessage = getErrorMessage(error, fallbackMessage);
    const hasChineseInError = /[\u4e00-\u9fff]/.test(errorMessage);
    const displayMessage =
      i18n.language.startsWith('zh') || !hasChineseInError
        ? errorMessage
        : fallbackMessage;

    if (status === 404) {
      return (
        <Card>
          <Empty
            description={t(
              'project:taskContent.spoProcess.overview.notCreated'
            )}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              onClick={() => navigate(`/project/${projectId}`)}
            >
              {t('project:taskContent.spoProcess.overview.backToProjectDetail')}
            </Button>
          </Empty>
        </Card>
      );
    }

    return (
      <Alert
        message={t('common:message.loadError')}
        description={displayMessage}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => refetch()}>
            {t('common:action.retry')}
          </Button>
        }
      />
    );
  }

  // 渲染空状态
  if (!spoData) {
    return (
      <Card>
        <Empty
          description={t('project:taskContent.spoProcess.overview.notCreated')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            onClick={() => navigate(`/project/${projectId}`)}
          >
            {t('project:taskContent.spoProcess.overview.backToProjectDetail')}
          </Button>
        </Empty>
      </Card>
    );
  }

  const quickActions = getQuickActions();

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题和操作 */}
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h2 style={{ margin: 0, marginBottom: 8 }}>
            {t('project:taskContent.spoProcess.overview.header.title')}
          </h2>
          <Space>
            <span style={{ color: '#666' }}>
              {t('project:taskContent.spoProcess.overview.header.spoId', {
                id: spoData._id,
              })}
            </span>
            <UnifiedStatusTag type="spoStatus" status={spoData.currentStatus} />
          </Space>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            {t('common:action.refresh')}
          </Button>
          <Button onClick={() => navigate(`/project/${projectId}`)}>
            {t('project:taskContent.spoProcess.overview.header.backToProject')}
          </Button>
        </Space>
      </div>

      {/* 整体进度卡片 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title={t(
                'project:taskContent.spoProcess.overview.stats.overallProgress'
              )}
              value={overallProgress}
              suffix="%"
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title={t(
                'project:taskContent.spoProcess.overview.stats.currentPhase'
              )}
              value={t(
                'project:taskContent.spoProcess.overview.basicInfo.phaseLabel',
                { phase: currentPhase }
              )}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title={t(
                'project:taskContent.spoProcess.overview.stats.emailCommunications'
              )}
              value={spoData.communications?.length || 0}
              suffix={t(
                'project:taskContent.spoProcess.overview.stats.emailUnit'
              )}
              prefix={<MailOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title={t(
                'project:taskContent.spoProcess.overview.stats.createdAt'
              )}
              value={new Date(spoData.createdAt).toLocaleDateString(
                i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US'
              )}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>

        <Divider />

        <Progress
          percent={overallProgress}
          strokeColor={{
            '0%': '#10b981',
            '100%': '#059669',
          }}
          status={spoData.currentStatus === 'COMPLETED' ? 'success' : 'active'}
        />
      </Card>

      {/* 快速操作 */}
      {quickActions.length > 0 && (
        <Card
          title={t(
            'project:taskContent.spoProcess.overview.quickActions.title'
          )}
          style={{ marginBottom: 24 }}
        >
          <Space wrap>
            {quickActions.map(action => (
              <Button
                key={action.key}
                type={action.type || 'default'}
                icon={action.icon}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
          </Space>
        </Card>
      )}

      {/* 三阶段流程 */}
      <Card
        title={t('project:taskContent.spoProcess.overview.phases.title')}
        style={{ marginBottom: 24 }}
      >
        <Steps current={currentPhase - 1} style={{ marginBottom: 32 }}>
          <Step
            title={t(
              'project:taskContent.spoProcess.overview.phases.phase1Title'
            )}
            description={t(
              'project:taskContent.spoProcess.overview.phases.phase1Description',
              {
                status: spoData.engagementLetter.status,
              }
            )}
            icon={<FileTextOutlined />}
            status={
              currentPhase > 1
                ? 'finish'
                : currentPhase === 1
                  ? 'process'
                  : 'wait'
            }
          />
          <Step
            title={t(
              'project:taskContent.spoProcess.overview.phases.phase2Title'
            )}
            description={t(
              'project:taskContent.spoProcess.overview.phases.phase2Description',
              {
                status: spoData.evaluation.status,
              }
            )}
            icon={<FormOutlined />}
            status={
              currentPhase > 2
                ? 'finish'
                : currentPhase === 2
                  ? 'process'
                  : 'wait'
            }
          />
          <Step
            title={t(
              'project:taskContent.spoProcess.overview.phases.phase3Title'
            )}
            description={t(
              'project:taskContent.spoProcess.overview.phases.phase3Description',
              {
                status: spoData.result.status,
              }
            )}
            icon={<FileDoneOutlined />}
            status={currentPhase === 3 ? 'process' : 'wait'}
          />
        </Steps>

        {/* 阶段详情 */}
        <Row gutter={16}>
          {/* Phase 1 */}
          <Col xs={24} md={8}>
            <Card
              size="small"
              title={t(
                'project:taskContent.spoProcess.overview.phases.phase1Title'
              )}
              extra={
                <Tag color={currentPhase >= 1 ? 'green' : 'default'}>
                  {t(
                    'project:taskContent.spoProcess.overview.phases.phase1Short'
                  )}
                </Tag>
              }
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/spo/${spoData._id}/engagement-letter`)}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.status'
                  )}
                >
                  <UnifiedStatusTag
                    type="elStatus"
                    status={spoData.engagementLetter.status}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.progress'
                  )}
                >
                  <Progress
                    percent={spoData.engagementLetter.progress}
                    size="small"
                    strokeColor="#10b981"
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.payment'
                  )}
                >
                  <UnifiedStatusTag
                    type="paymentStatus"
                    status={spoData.paymentProof?.status || 'PENDING'}
                  />
                </Descriptions.Item>
              </Descriptions>
              <Button
                type="link"
                icon={<RightOutlined />}
                style={{ padding: 0, marginTop: 8 }}
              >
                {t(
                  'project:taskContent.spoProcess.overview.phases.viewDetails'
                )}
              </Button>
            </Card>
          </Col>

          {/* Phase 2 */}
          <Col xs={24} md={8}>
            <Card
              size="small"
              title={t(
                'project:taskContent.spoProcess.overview.phases.phase2Title'
              )}
              extra={
                <Tag color={currentPhase >= 2 ? 'green' : 'default'}>
                  {t(
                    'project:taskContent.spoProcess.overview.phases.phase2Short'
                  )}
                </Tag>
              }
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/spo/${spoData._id}/evaluation`)}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.status'
                  )}
                >
                  <UnifiedStatusTag
                    type="evaluationStatus"
                    status={spoData.evaluation.status}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.progress'
                  )}
                >
                  <Progress
                    percent={spoData.evaluation.progress}
                    size="small"
                    strokeColor="#10b981"
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.framework'
                  )}
                >
                  {spoData.evaluation.framework ? (
                    <Tag color="green">
                      {t(
                        'project:taskContent.spoProcess.overview.phases.generated'
                      )}
                    </Tag>
                  ) : (
                    <Tag>
                      {t(
                        'project:taskContent.spoProcess.overview.phases.notGenerated'
                      )}
                    </Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
              <Button
                type="link"
                icon={<RightOutlined />}
                style={{ padding: 0, marginTop: 8 }}
              >
                {t(
                  'project:taskContent.spoProcess.overview.phases.viewDetails'
                )}
              </Button>
            </Card>
          </Col>

          {/* Phase 3 */}
          <Col xs={24} md={8}>
            <Card
              size="small"
              title={t(
                'project:taskContent.spoProcess.overview.phases.phase3Title'
              )}
              extra={
                <Tag color={currentPhase >= 3 ? 'green' : 'default'}>
                  {t(
                    'project:taskContent.spoProcess.overview.phases.phase3Short'
                  )}
                </Tag>
              }
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/spo/${spoData._id}/result`)}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.status'
                  )}
                >
                  <UnifiedStatusTag
                    type="resultStatus"
                    status={spoData.result.status}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.progress'
                  )}
                >
                  <Progress
                    percent={spoData.result.progress}
                    size="small"
                    strokeColor="#10b981"
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    'project:taskContent.spoProcess.overview.phases.draft'
                  )}
                >
                  <Tag>
                    {t(
                      'project:taskContent.spoProcess.overview.phases.draftCount',
                      {
                        count: spoData.result.drafts?.length || 0,
                      }
                    )}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
              <Button
                type="link"
                icon={<RightOutlined />}
                style={{ padding: 0, marginTop: 8 }}
              >
                {t(
                  'project:taskContent.spoProcess.overview.phases.viewDetails'
                )}
              </Button>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* 时间线 */}
      <Card
        title={t(
          'project:taskContent.spoProcess.overview.recentActivity.title'
        )}
        style={{ marginBottom: 24 }}
      >
        {recentEvents.length > 0 ? (
          <Timeline>
            {recentEvents.map((event, index) => (
              <Timeline.Item
                key={index}
                color={index === 0 ? 'green' : 'gray'}
                dot={index === 0 ? <CheckCircleOutlined /> : undefined}
              >
                <p style={{ margin: 0, fontWeight: index === 0 ? 600 : 400 }}>
                  {event.description}
                </p>
                <p style={{ margin: 0, color: '#666', fontSize: '12px' }}>
                  {new Date(event.timestamp).toLocaleString(
                    i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US'
                  )}{' '}
                  - {event.actor.username} ({event.actor.role})
                </p>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <Empty
            description={t(
              'project:taskContent.spoProcess.overview.recentActivity.empty'
            )}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
        <Button
          type="link"
          onClick={() => navigate(`/spo/${spoData._id}/timeline`)}
          style={{ padding: 0, marginTop: 16 }}
        >
          {t(
            'project:taskContent.spoProcess.overview.recentActivity.viewFullTimeline'
          )}{' '}
          <RightOutlined />
        </Button>
      </Card>

      {/* 基本信息 */}
      <Card
        title={t('project:taskContent.spoProcess.overview.basicInfo.title')}
      >
        <Descriptions column={2} bordered>
          <Descriptions.Item
            label={t('project:taskContent.spoProcess.overview.basicInfo.spoId')}
          >
            {spoData._id}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              'project:taskContent.spoProcess.overview.basicInfo.projectId'
            )}
          >
            {spoData.projectId}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              'project:taskContent.spoProcess.overview.basicInfo.currentStatus'
            )}
          >
            <UnifiedStatusTag type="spoStatus" status={spoData.currentStatus} />
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              'project:taskContent.spoProcess.overview.basicInfo.currentPhase'
            )}
          >
            {t('project:taskContent.spoProcess.overview.basicInfo.phaseLabel', {
              phase: spoData.currentPhase,
            })}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              'project:taskContent.spoProcess.overview.basicInfo.issuer'
            )}
          >
            {spoData.issuerUserId || '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              'project:taskContent.spoProcess.overview.basicInfo.operator'
            )}
          >
            {spoData.operatorUserId || '-'}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              'project:taskContent.spoProcess.overview.basicInfo.createdAt'
            )}
          >
            {new Date(spoData.createdAt).toLocaleString(
              i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US'
            )}
          </Descriptions.Item>
          <Descriptions.Item
            label={t(
              'project:taskContent.spoProcess.overview.basicInfo.updatedAt'
            )}
          >
            {new Date(spoData.updatedAt).toLocaleString(
              i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US'
            )}
          </Descriptions.Item>
          {spoData.completedAt && (
            <Descriptions.Item
              label={t(
                'project:taskContent.spoProcess.overview.basicInfo.completedAt'
              )}
            >
              {new Date(spoData.completedAt).toLocaleString(
                i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US'
              )}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    </div>
  );
};

export default SPOProcessPage;
